import { DataVisualization } from "@/types/colors";
import {
  CreateModelScopeSettingsItem,
  CreateModelScopeSettingsItemsRequest,
  DeleteModelScopeSettingsItem,
  DeleteModelScopeSettingsItemsRequest,
  ModelScopeSettingType,
  ModelScopeSettingsItemRespone,
  SoftDeleteStatusType,
  UpdateModelScopeSettingsItem,
  UpdateModelScopeSettingsItemsRequest,
} from "@api/apiClient";
import { useCreateModelScopeSettingItems, useDeleteModelScopeSettingItems, useModelScopeSetting, useUpdateModelScopeSettingItems } from "@api/modelScopes";
import ColorPicker from "@components/ColorPicker/ColorPicker";
import ColorPickerSwatch from "@components/ColorPicker/ColorPickerSwatch";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ResetIcon from "@mui/icons-material/SettingsBackupRestore";
import { Box, Button, Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridSlotsComponentsProps,
  GridToolbarContainer,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

export interface ModelScopesGridProps {
  mode: ModelScopeSettingType;
  modelScopeSettingId: string | undefined;
}

const ModelScopesGrid = observer((props: ModelScopesGridProps) => {
  const [showArchived, setShowArchived] = useState(false);
  const [addDisabled, setAddDisabled] = useState(false);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [rows, setRows] = useState<ModelScopeSettingsItemRespone[]>([]);
  const modelScopeSetting = useModelScopeSetting(props.mode, props.modelScopeSettingId, {
    enabled: !!props.modelScopeSettingId,
  });
  const createModelScopeSettingItems = useCreateModelScopeSettingItems();
  const updateModelScopeSettingItems = useUpdateModelScopeSettingItems();
  const deleteModelScopeSettingItems = useDeleteModelScopeSettingItems();
  const apiRef = useGridApiRef();
  // using flex on columns causes resize issues with are
  // resolved with the call to autosizeColumns
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const [coordinates, setCoordinates] = useState({ rowIndex: 0, colIndex: 0 });

  useEffect(() => {
    apiRef.current.scrollToIndexes(coordinates);
    apiRef.current.setCellFocus(coordinates.rowIndex, "name");
  }, [apiRef, coordinates]);

  useEffect(() => {
    if (modelScopeSetting.data?.modelScopeSettingsItems) {
      setRows(modelScopeSetting.data.modelScopeSettingsItems.filter((r: ModelScopeSettingsItemRespone) => showArchived || r.deleteStatus === SoftDeleteStatusType.Active));
    }
  }, [modelScopeSetting.data, showArchived]);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: i18n.t("model-scopes-tab.grid.columns.id"),
      editable: false,
      flex: 1,
    },
    {
      field: "name",
      headerName: i18n.t("model-scopes-tab.grid.columns.name"),
      editable: true,
      flex: 1,
    },
    {
      field: "description",
      headerName: i18n.t("model-scopes-tab.grid.columns.description"),
      editable: true,
      flex: 1,
    },
    {
      field: "color",
      headerName: i18n.t("model-scopes-tab.grid.columns.color"),
      editable: true,
      width: 100,
      renderEditCell: (params: any) => {
        console.log("renderEditCell params: ", params);
        const { id, value, field } = params;

        const onChange = async (color: string) => {
          console.log("Color changed: ", color);
          const argb = parseInt(color.substr(1), 16);
          console.log("argb: ", argb);

          await params.api.setEditCellValue({ id, field, value: argb, debounceMs: 500 });
          params.api.stopCellEditMode({ id, field });
        };

        return (
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              alignItems: "center",
              padding: "0 10px",
              height: "100%",
            }}
          >
            <ColorPicker color={value} onChange={onChange} />
          </Box>
        );
      },
      renderCell: (params: any) => {
        console.log("renderCell params: ", params);
        const { id, value, field } = params;

        const onOpen = () => {
          console.log("onOpen: ", id);
          const rowMode = params.api.getRowMode(id);
          if (rowMode !== GridRowModes.Edit) {
            params.api.startRowEditMode({ id });
          }
          params.api.startCellEditMode({ id, field });
        };

        return (
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              alignItems: "center",
              padding: "0 10px",
              height: "100%",
            }}
          >
            <ColorPickerSwatch color={value} onOpen={onOpen} />
          </Box>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("model-scopes-tab.grid.columns.actions"),
      resizable: false,
      width: 100,
      getActions: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={`save-${id}`}
              icon={
                <Tooltip title={i18n.t("model-scopes-tab.grid.save-button.tooltip")}>
                  <SaveIcon />
                </Tooltip>
              }
              label={i18n.t("model-scopes-tab.grid.save-button.label")}
              onClick={() => handleSaveClick(id)}
              sx={{
                color: "primary.main",
              }}
            />,
            <GridActionsCellItem
              key={`cancel-${id}`}
              icon={
                <Tooltip title={i18n.t("model-scopes-tab.grid.cancel-button.tooltip")}>
                  <CancelIcon />
                </Tooltip>
              }
              label={i18n.t("model-scopes-tab.grid.cancel-button.label")}
              onClick={() => handleCancelClick(id)}
            />,
          ];
        } else if (row.status === "Deleted") {
          return [
            <GridActionsCellItem
              key={`restore-${id}`}
              icon={
                <Tooltip title={i18n.t("model-scopes-tab.grid.restore-button.tooltip")}>
                  <ResetIcon />
                </Tooltip>
              }
              label={i18n.t("model-scopes-tab.grid.restore-button.label")}
              onClick={() => handleRestoreClick(id, row)}
            />,
          ];
        } else {
          return [
            <GridActionsCellItem
              key={`edit-${id}`}
              icon={
                <Tooltip title={i18n.t("model-scopes-tab.grid.edit-button.tooltip")}>
                  <EditIcon />
                </Tooltip>
              }
              label={i18n.t("model-scopes-tab.grid.edit-button.label")}
              onClick={() => handleEditClick(id)}
            />,
            <GridActionsCellItem
              key={`delete-${id}`}
              icon={
                <Tooltip title={i18n.t("model-scopes-tab.grid.delete-button.tooltip")}>
                  <DeleteIcon />
                </Tooltip>
              }
              label={i18n.t("model-scopes-tab.grid.delete-button.label")}
              onClick={() => handleDeleteClick(id, row)}
            />,
          ];
        }
      },
    },
  ];

  const handleAddRow = () => {
    const modelScopeSettingsItem = new ModelScopeSettingsItemRespone({
      nodePath: "",
      id: -1,
      name: "name",
      description: "description",
      color: parseInt(DataVisualization.Colors.getNextColor(DataVisualization.Colors.Q5Palette, Math.max(0, rows.length - 1)).substr(1), 16),
    });
    //setRowModesModel({ ...rowModesModel, [-1]: { mode: GridRowModes.Edit } });
    setRows((oldRows: any) => [{ ...modelScopeSettingsItem, isNew: true }, ...oldRows]);
    setAddDisabled(true);
    setTimeout(() => {
      apiRef.current.startRowEditMode({ id: -1 });
      setTimeout(() => {
        setCoordinates({ rowIndex: 0, colIndex: 0 });
      }, 1000);
    });
  };

  const handleShowArchived = (showArchived: boolean) => {
    setShowArchived(showArchived);
  };

  const handleEditClick = async (id: GridRowId) => {
    console.log("Edit clicked: ", id);
    apiRef.current.startRowEditMode({ id });
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleDeleteClick = async (id: GridRowId, row: any) => {
    console.log("Delete clicked: ", id);

    if (row?.id === 0) {
      throw new Error("Unable to delete default row.");
    }

    await deleteModelScopeSettingItems.mutateAsync(
      new DeleteModelScopeSettingsItemsRequest({
        modelScopeSettingId: props.modelScopeSettingId!,
        modelScopeSettingsItems: [new DeleteModelScopeSettingsItem(row)],
      }) as any,
    ); // YUKK - FIX ME
  };

  const handleRestoreClick = async (_id: GridRowId, row: any) => {
    await updateModelScopeSettingItems.mutateAsync(
      new UpdateModelScopeSettingsItemsRequest({
        modelScopeSettingId: props.modelScopeSettingId!,
        modelScopeSettingsItems: [new UpdateModelScopeSettingsItem({ ...row, deleteStatus: SoftDeleteStatusType.Active })],
      }) as any,
    ); // YUKK - FIX ME
  };

  const handleSaveClick = async (id: GridRowId) => {
    console.log("Save clicked: ", id);
    setAddDisabled(false);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => {
    console.log("Cancel clicked: ", id);
    setAddDisabled(false);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } });

    const newRows = rows.filter((r: any) => r.isNew !== true);
    setRows(newRows);
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    console.log("handleRowModesModelChange: ", newRowModesModel);
    setRowModesModel(newRowModesModel);
  };

  const handleCellEditStart: GridEventListener<"cellEditStart"> = (params, _event) => {
    console.log("handleCellEditStart: ", params);
    setAddDisabled(true);
  };

  const handleCellEditStop: GridEventListener<"cellEditStop"> = (params, _event) => {
    console.log("handleCellEditStop: ", params);
    setAddDisabled(false);
  };

  const handleRowEditStart: GridEventListener<"rowEditStart"> = (params, _event) => {
    console.log("handleRowEditStart: ", params);
    setAddDisabled(true);
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (params, event) => {
    console.log("handleRowEditStop: ", params);

    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }

    setAddDisabled(rows.some((r: any) => r.isNew === true));
  };

  const processRowUpdate = async (newRow: GridRowModel, oldRow: GridRowModel) => {
    console.log("processRowUpdate: ", oldRow, newRow);

    // TODO: here we can validate the row and return null if it is not valid

    if (newRow.ignoreModifications) {
      return newRow;
    }

    if (newRow.id === -1) {
      await createModelScopeSettingItems.mutateAsync(
        new CreateModelScopeSettingsItemsRequest({
          modelScopeSettingId: props.modelScopeSettingId!,
          modelScopeSettingsItems: [new CreateModelScopeSettingsItem(newRow)],
        }) as any,
      ); // YUKK - FIX ME
    } else {
      await updateModelScopeSettingItems.mutateAsync(
        new UpdateModelScopeSettingsItemsRequest({
          modelScopeSettingId: props.modelScopeSettingId!,
          modelScopeSettingsItems: [new UpdateModelScopeSettingsItem(newRow)],
        }) as any,
      ); // YUKK - FIX ME
    }

    return newRow;
  };

  return (
    <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
      <DataGrid
        apiRef={apiRef}
        loading={modelScopeSetting.isFetching}
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        hideFooter={false}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onCellEditStart={handleCellEditStart}
        onCellEditStop={handleCellEditStop}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => console.error("onProcessRowUpdateError: ", error)}
        slots={{ toolbar: EditToolbar }}
        slotProps={{ toolbar: { onAddRow: handleAddRow, onShowArchived: handleShowArchived, disabled: addDisabled, showArchived } }}
        sx={{ height: `${(height || 400) - 16}px` }}
      />
    </Box>
  );
});

declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    showArchived: boolean;
    disabled: boolean;
    onAddRow: () => void;
    onShowArchived: (showArchived: boolean) => void;
  }
}

const EditToolbar = (props: NonNullable<GridSlotsComponentsProps["toolbar"]>) => {
  //console.log("EditToolbar props: ", props);

  return (
    <GridToolbarContainer>
      <Tooltip title={i18n.t("model-scopes-tab.grid.add-button.tooltip")}>
        <Button variant="outlined" onClick={() => props.onAddRow?.()} disabled={props.disabled}>
          {i18n.t("model-scopes-tab.grid.add-button.label")}
        </Button>
      </Tooltip>
      <Box sx={{ flexGrow: 1 }} />
      <Box>
        <FormControlLabel
          control={<Checkbox checked={props.showArchived} onChange={(_, checked) => props.onShowArchived?.(checked)} size="small" />}
          label={i18n.t("model-scopes-tab.grid.show-archived.label")}
          disabled={props.disabled}
          slotProps={{
            typography: { variant: "body2" },
          }}
        />
      </Box>
    </GridToolbarContainer>
  );
};

export default ModelScopesGrid;
